<template>
	<div ref="tabContainerElementRef">
		<div
			:class="[fixTabClass, 'top-0 transition-all duration-300']"
			:style="{
				top: `${topDistanceOfTab}px`,
			}"
		>
			<BaseTabs
				ref="tabInnerContainerElementRef"
				size="medium"
				type="secondary"
				:class="[fixTabClassInner, 'overflow-x-auto']"
			>
				<BaseTabItem
					v-for="tab of tabs"
					:key="tab.label"
					:ref="el => updateTabElementRefs(tab.value, el)"
					:isSelected="formattedSelectTab === tab.route"
					type="secondary"
					size="medium"
					@click="handleCurrentTab(tab.route)"
				>
					{{ tab.label }}
				</BaseTabItem>
			</BaseTabs>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	onMounted,
	onBeforeUnmount,
	computed,
	watch,
	type PropType,
} from 'vue'
import BaseTabItem from '@/components/ui/baseTab/BaseTabItem.vue'
import BaseTabs from '@/components/ui/baseTab/BaseTabs.vue'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import { capitalizeFirstLetter } from '@/lib/utils'
import scrollState from '@/assets/ts/checkScroll.ts'
import { getPostListModeMapping } from '@/legacy/ts/productSharing'

type Tab = {
	label: string
	value: string
	route: string
}

const props = defineProps({
	tabs: {
		type: Array as PropType<Tab[]>,
		required: true,
	},
	selectTab: {
		type: String,
		required: true,
	},
	currentBannerTab: {
		type: String,
		default: '',
	},
	currentSecondaryTab: {
		type: String,
		default: '',
	},
	currentCategoryAlias: {
		type: String,
		default: '',
	},
	currentWarehouseAlias: {
		type: String,
		default: '',
	},
	sortMode: {
		type: String,
	},
	currentSearchParameter: {
		type: String,
	},
})
const emits = defineEmits(['update:selectTab'])

const tabElementRefs = ref<{ [key: string]: HTMLElement | null }>({})
const tabContainerElementRef = ref<HTMLElement | null>(null)
const tabInnerContainerElementRef = ref<InstanceType<typeof BaseTabs> | null>(
	null,
)
const isShowTabFixStyle = ref(false)
const tabObserver = ref<IntersectionObserver | null>(null)
const fixTabClass = computed(() => {
	return isShowTabFixStyle.value
		? 'fixed left-0 right-0 bg-white px-20px py-12px mdp:px-32px lg:px-0 lg:py-16px z-[2] shadow-[0_4px_10px_0px_rgba(0,0,0,0.05)]'
		: 'px-20px pt-24px mdp:px-32px mdp:pt-32px lg:px-0 lg:w-[1204px] lg:mx-auto'
})
const fixTabClassInner = computed(() => {
	return isShowTabFixStyle.value ? 'lg:w-[1204px] mx-auto' : ''
})
const topDistanceOfTab = ref(0)
const bsHeader = ref<HTMLElement | null>(null)
const headerOffsetTop = ref(0)
const formattedSelectTab = computed(() => {
	const dict = getPostListModeMapping('name')
	return dict[props.selectTab] || props.selectTab
})

onMounted(() => {
	initialTabObserver()
	scrollSelectedTabToCenter()
	bsHeader.value = document.querySelector('.bs-header')
})

onBeforeUnmount(() => {
	clearTabObserver()
})

watch(
	() => scrollState.currentScrollPos,
	(newVal: number) => {
		if (!isShowTabFixStyle.value) return 0
		if (newVal < scrollState.MIN_SCROLL_DISTANCE) {
			topDistanceOfTab.value = 0
		} else if (scrollState.isScrollingUp) {
			headerOffsetTop.value = bsHeader.value?.getBoundingClientRect()?.top ?? 0
			topDistanceOfTab.value = scrollState.headerHeight + headerOffsetTop.value
		} else {
			topDistanceOfTab.value = 0
		}
		return 0
	},
)

function initialTabObserver() {
	if (!tabContainerElementRef.value) return

	tabObserver.value = new IntersectionObserver(
		(entries, observer) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					isShowTabFixStyle.value = false
				} else if (
					tabContainerElementRef.value &&
					tabContainerElementRef.value.getBoundingClientRect()?.top < 0
				) {
					isShowTabFixStyle.value = true
				}
			})
		},
		{
			threshold: 1,
		},
	)

	tabObserver.value.observe(tabContainerElementRef.value)
}

function clearTabObserver() {
	if (tabObserver.value) {
		tabObserver.value.disconnect()
	}
}

function updateTabElementRefs(
	tab: string,
	el: InstanceType<typeof BaseTabs> | null,
) {
	if (!el) {
		tabElementRefs.value[tab] = null
		return
	}
	tabElementRefs.value[tab] = el.$el
}

function handleCurrentTab(tab: string) {
	emits('update:selectTab', tab === formattedSelectTab.value ? '' : tab)
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.CommunityPostListingPage,
		button_name: 'Secondary Tab',
		tab_name: `${capitalizeFirstLetter(
			props.currentBannerTab,
		)}-${capitalizeFirstLetter(props.currentSecondaryTab)}`,
		sort_option: `${capitalizeFirstLetter(props.sortMode ?? '')}`,
		warehouse_filter: `${props.currentWarehouseAlias}`,
		category_filter: `${props.currentCategoryAlias}`,
		keyword: `${props.currentSearchParameter}`,
	})
}

function scrollSelectedTabToCenter() {
	const currentSelectTab = tabElementRefs.value[props.selectTab]
	const tabContainer = tabInnerContainerElementRef.value?.$el

	if (!!currentSelectTab && !!tabContainer) {
		tabContainer.scroll({
			left: currentSelectTab.offsetLeft - tabContainer.offsetLeft,
			behavior: 'smooth',
		})
	}
}
</script>
